import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import fileDownload from 'react-file-download';
import type { Location } from 'react-router-dom';
import dayjs from '~/timeInit';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridCellEditStopReasons,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridEditInputCell,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowModel,
  gridEditRowsStateSelector,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';
import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import { useExportCharterFareFamilyPricing } from '~/hooks/apis/flights-charter/useCharterFareFamilyPricing';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useAppNavigation from '~/hooks/useAppNavigation';

import {
  type FareFamilyPricingParams,
  fetchCharterFareFamilyPricing,
  updateCharterFareFamilyPricing,
} from '~/services/CharterService';
import { formatDateISO } from '~/services/TimeService';

import { charterFlightsPricingColumnGroupingModel, charterFlightsPricingColumns } from './CharterFlightsPricingColumns';
import {
  DEPARTING_ARR_AIRPORT,
  DEPARTING_DEP_AIRPORT,
  RETURNING_ARR_AIRPORT,
  RETURNING_DEP_AIRPORT,
  buildUpdatePayload,
  displayToPercent,
  formatCurrency,
  formatPercentDisplay,
  mapApiResultToRow,
  parseAndValidateRow,
  percentToDisplay,
} from './CharterFlightsPricingHelpers';
import CharterFlightsPricingToolbar from './CharterFlightsPricingToolbar';
import type { CharterPricingRow, PageInfo } from './CharterFlightsPricingTypes';
import { canEditCharterPricingDeparture, getCharterPricingToday } from './charterPricingDates';

export type { CharterPricingRow } from './CharterFlightsPricingTypes';

type PricingSearchFilters = Pick<FareFamilyPricingParams, 'departureDateTo' | 'direction'> & {
  departureDateFrom: string;
};

const PRICING_DIRECTION_LABELS = {
  departing: `${DEPARTING_DEP_AIRPORT} → ${DEPARTING_ARR_AIRPORT}`,
  returning: `${RETURNING_DEP_AIRPORT} → ${RETURNING_ARR_AIRPORT}`,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const COLUMN_HEADER_HEIGHT = 64;
const COLUMN_GROUP_HEADER_HEIGHT = 32;

const PERCENTAGE_FIELDS = new Set<keyof CharterPricingRow>([
  'packagedChildBusinessMultiplier',
  'packagedChildEconomyMultiplier',
  'packagedInfantBusinessMultiplier',
  'packagedInfantEconomyMultiplier',
  'standaloneReturnBusinessMarkup',
  'standaloneReturnEconomyMarkup',
  'standaloneOneWayBusinessMarkup',
  'standaloneOneWayEconomyMarkup',
]);

const EDITABLE_NUMERIC_FIELDS = charterFlightsPricingColumns
  .filter((column) => column.type === 'number' && column.editable)
  .map((column) => column.field as keyof CharterPricingRow);

function luxPlusFieldFor(field: keyof CharterPricingRow): keyof CharterPricingRow {
  return `luxPlus${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof CharterPricingRow;
}

export default function CharterFlightsPricingPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const { block, push } = useAppNavigation();
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 50 });
  const [departureDateFrom, setDepartureDateFrom] = useState<Dayjs | null>(() => dayjs(getCharterPricingToday()));
  const [departureDateTo, setDepartureDateTo] = useState<Dayjs | null>(null);
  const [direction, setDirection] = useState<FareFamilyPricingParams['direction'] | ''>('');
  const [searchFilters, setSearchFilters] = useState<PricingSearchFilters>(() => ({
    departureDateFrom: getCharterPricingToday(),
  }));
  const validDates = Boolean(
    departureDateFrom?.isValid() &&
    (!departureDateTo || (departureDateTo.isValid() && !departureDateTo.isBefore(departureDateFrom, 'day'))),
  );
  const filtersChanged =
    departureDateFrom?.format('YYYY-MM-DD') !== searchFilters.departureDateFrom ||
    (departureDateTo?.format('YYYY-MM-DD') || undefined) !== searchFilters.departureDateTo ||
    (direction || undefined) !== searchFilters.direction;
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableTop, setTableTop] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const exportPricingMutation = useExportCharterFareFamilyPricing();
  const [hasCellChanges, setHasCellChanges] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [editedRows, setEditedRows] = useState<Map<string, CharterPricingRow>>(new Map());
  const editedRowsRef = useRef(editedRows);
  const replaceEditedRows = useCallback((next: Map<string, CharterPricingRow>) => {
    editedRowsRef.current = next;
    setEditedRows(next);
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [isCommittingCell, setIsCommittingCell] = useState(false);
  const editedRowPagesRef = useRef<Map<string, PageInfo>>(new Map());
  const luxPlusTouchedFieldsRef = useRef<Map<string, Set<keyof CharterPricingRow>>>(new Map());
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const hasUnsavedChanges = editedRows.size > 0 || hasCellChanges || isCommittingCell;

  // Navigation blocking state
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  // Block navigation when there are unsaved changes
  useEffect(() => {
    if (hasUnsavedChanges || isSaving) {
      unblockRef.current = block((location) => {
        if (savingRef.current) {
          enqueueSnackbar('Please wait for the save to finish before leaving.', { variant: 'info' });
          return false;
        }
        setPendingNavigation(location);
        setIsDiscardDialogOpen(true);
        // Return false to prevent navigation
        return false;
      });
    } else {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [hasUnsavedChanges, isSaving, block, enqueueSnackbar]);

  // Block browser refresh/close when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges || isSaving) {
        event.preventDefault();
        // Modern browsers require returnValue to be set
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isSaving]);

  const {
    data: pricingData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      'charterFareFamilyPricing',
      DEPARTING_DEP_AIRPORT,
      DEPARTING_ARR_AIRPORT,
      RETURNING_DEP_AIRPORT,
      RETURNING_ARR_AIRPORT,
      searchFilters,
      offset,
      limit,
    ],
    queryFn: async () => {
      return await fetchCharterFareFamilyPricing({
        departingDepAirport: DEPARTING_DEP_AIRPORT,
        departingArrAirport: DEPARTING_ARR_AIRPORT,
        returningDepAirport: RETURNING_DEP_AIRPORT,
        returningArrAirport: RETURNING_ARR_AIRPORT,
        ...searchFilters,
        offset,
        limit,
      });
    },
  });

  const updatePricingMutation = useMutation({
    mutationFn: updateCharterFareFamilyPricing,
  });

  const rows = useMemo<Array<CharterPricingRow>>(() => {
    if (!pricingData?.result) {
      return [];
    }
    return pricingData.result.flatMap((item) => {
      const baseRow = mapApiResultToRow(item);
      const row = editedRows.get(baseRow.flightInstanceId) ?? baseRow;
      const luxPlusRow = {
        ...row,
        id: `${row.flightInstanceId}-luxplus`,
        pricingType: 'LuxPlus' as const,
      };

      for (const field of EDITABLE_NUMERIC_FIELDS) {
        luxPlusRow[field] = row[luxPlusFieldFor(field)] as never;
      }

      return [{ ...row, id: `${row.flightInstanceId}-standard`, pricingType: 'Standard' }, luxPlusRow];
    });
  }, [pricingData, editedRows]);

  const totalRowCount = pricingData?.pagination?.total_items ?? 0;

  const handleConfirmDiscard = useCallback(() => {
    // Clear all edited state
    for (const [id, fields] of Object.entries(gridEditRowsStateSelector(apiRef.current.state) ?? {})) {
      for (const field of Object.keys(fields)) {
        if (apiRef.current.getCellMode(id, field) === GridCellModes.Edit) {
          apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
        }
      }
    }
    setIsCommittingCell(false);
    setHasCellChanges(false);
    replaceEditedRows(new Map());
    editedRowPagesRef.current.clear();
    luxPlusTouchedFieldsRef.current.clear();
    setCellModesModel({});
    setIsDiscardDialogOpen(false);
    setIsEditing(false);

    // Unblock navigation before navigating
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    if (pendingNavigation) {
      // Navigate to the pending location
      const targetPath = pendingNavigation.pathname + pendingNavigation.search + pendingNavigation.hash;
      setPendingNavigation(null);
      push(targetPath);
    }
  }, [apiRef, replaceEditedRows, pendingNavigation, push]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingNavigation(null);
  }, []);

  const updateEditedRow = useCallback(
    (row: CharterPricingRow) => {
      const baseRow = pricingData?.result
        ?.map(mapApiResultToRow)
        .find((item) => item.flightInstanceId === row.flightInstanceId);
      const unchanged =
        baseRow &&
        (Object.keys(baseRow) as Array<keyof CharterPricingRow>).every(
          (field) => field === 'id' || field === 'pricingType' || row[field] === baseRow[field],
        );
      const updated = new Map(editedRowsRef.current);
      if (unchanged) {
        updated.delete(row.flightInstanceId);
        editedRowPagesRef.current.delete(row.flightInstanceId);
        luxPlusTouchedFieldsRef.current.delete(row.flightInstanceId);
      } else {
        updated.set(row.flightInstanceId, row);
        editedRowPagesRef.current.set(row.flightInstanceId, { offset, limit });
      }
      replaceEditedRows(updated);
    },
    [pricingData, offset, limit, replaceEditedRows],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<CharterPricingRow>, oldRow: GridRowModel<CharterPricingRow>) => {
      setIsCommittingCell(false);
      setHasCellChanges(false);
      if (!isEditing || savingRef.current || !canEditCharterPricingDeparture(newRow.departureDate)) return oldRow;
      if (newRow.pricingType === 'LuxPlus') {
        const baseRow = pricingData?.result
          ?.map(mapApiResultToRow)
          .find((row) => row.flightInstanceId === newRow.flightInstanceId);
        const currentRow = editedRowsRef.current.get(newRow.flightInstanceId) ?? baseRow;
        if (!currentRow) {
          return newRow;
        }

        const updatedRow = { ...currentRow };
        let hasActualChanges = false;

        for (const field of EDITABLE_NUMERIC_FIELDS) {
          if (newRow[field] === oldRow[field]) {
            continue;
          }

          const luxPlusField = luxPlusFieldFor(field);
          const value = newRow[field];
          if (value == null || value === '') {
            updatedRow[luxPlusField] = null as never;
          } else {
            const validatedRow = parseAndValidateRow({ ...currentRow, [field]: value } as CharterPricingRow);
            updatedRow[luxPlusField] = validatedRow[field] as never;
          }
          hasActualChanges = true;
          const touchedFields =
            luxPlusTouchedFieldsRef.current.get(newRow.flightInstanceId) ?? new Set<keyof CharterPricingRow>();
          touchedFields.add(luxPlusField);
          luxPlusTouchedFieldsRef.current.set(newRow.flightInstanceId, touchedFields);
        }

        if (hasActualChanges) {
          updateEditedRow(updatedRow);
        }

        return newRow;
      }

      const validatedRow = parseAndValidateRow(newRow);

      // Check if any editable field actually changed
      const hasActualChanges = (Object.keys(validatedRow) as Array<keyof CharterPricingRow>).some((key) => {
        return validatedRow[key] !== oldRow[key];
      });

      if (hasActualChanges) {
        updateEditedRow(validatedRow);
      }

      return validatedRow;
    },
    [isEditing, updateEditedRow, pricingData],
  );

  const handleProcessRowUpdateError = useCallback(
    (err: Error) => {
      setIsCommittingCell(false);
      enqueueSnackbar(`Invalid value: ${err.message}`, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  // Single-click editing: enter edit mode on cell click when not in view mode
  const handleCellClick = useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal clicks
    if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
      return;
    }

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of cells from other rows to view
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row to view
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleToggleBooleanField = useCallback(
    (
      rowId: string,
      field: keyof Pick<
        CharterPricingRow,
        | 'offeredForSale'
        | 'packagedAdultEconomyFree'
        | 'packagedChildEconomyFree'
        | 'luxPlusPackagedAdultEconomyFree'
        | 'luxPlusPackagedChildEconomyFree'
      >,
      currentValue: boolean | null,
    ) => {
      if (!isEditing || savingRef.current || isFetching) return;
      const baseRow = pricingData?.result?.map(mapApiResultToRow).find((row) => row.id === rowId);
      const row = editedRowsRef.current.get(rowId) ?? baseRow;
      if (!row || !canEditCharterPricingDeparture(row.departureDate)) return;
      const updatedRow: CharterPricingRow = { ...row, [field]: !currentValue };
      if (field.startsWith('luxPlus')) {
        const touchedFields = luxPlusTouchedFieldsRef.current.get(rowId) ?? new Set<keyof CharterPricingRow>();
        touchedFields.add(field);
        luxPlusTouchedFieldsRef.current.set(rowId, touchedFields);
      }
      updateEditedRow(updatedRow);
    },
    [isEditing, isFetching, pricingData, updateEditedRow],
  );

  const columns = useMemo<Array<GridColDef<CharterPricingRow>>>(() => {
    return charterFlightsPricingColumns.map((col) => {
      // `unstable_rowSpanning` otherwise groups equal values in every column.
      // A row ID makes the pricing cells deliberately unique; only the shared
      // departure metadata below is allowed to span both pricing rows.
      const unspannedColumn = {
        ...col,
        rowSpanValueGetter: (_value: unknown, row: CharterPricingRow) => row.id,
      };

      if (col.field === 'offeredForSale') {
        return {
          ...unspannedColumn,
          rowSpanValueGetter: (_value: unknown, row: CharterPricingRow) => row.flightInstanceId,
          renderCell: (params: GridRenderCellParams<CharterPricingRow, boolean>) => (
            <Switch
              checked={!!params.value}
              disabled={
                !isEditing || isSaving || isFetching || !canEditCharterPricingDeparture(params.row.departureDate)
              }
              onChange={() => handleToggleBooleanField(params.row.flightInstanceId, 'offeredForSale', !!params.value)}
              size="small"
            />
          ),
        };
      }
      if (col.field === 'pricingType') {
        return {
          ...unspannedColumn,
          renderCell: (params: GridRenderCellParams<CharterPricingRow, string>) => (
            <Typography variant="caption" color={params.value === 'LuxPlus' ? 'primary.main' : 'text.primary'}>
              {params.value}
            </Typography>
          ),
        };
      }
      if (col.field === 'packagedAdultEconomyFree' || col.field === 'packagedChildEconomyFree') {
        const luxPlusField = `luxPlus${col.field.charAt(0).toUpperCase()}${col.field.slice(1)}` as
          | 'luxPlusPackagedAdultEconomyFree'
          | 'luxPlusPackagedChildEconomyFree';
        return {
          ...unspannedColumn,
          renderCell: (params: GridRenderCellParams<CharterPricingRow, boolean>) => (
            <Checkbox
              inputProps={{ 'aria-label': `${params.row.pricingType} ${col.headerName}` }}
              checked={params.row.pricingType === 'LuxPlus' ? params.row[luxPlusField] === true : !!params.value}
              disabled={
                !isEditing || isSaving || isFetching || !canEditCharterPricingDeparture(params.row.departureDate)
              }
              onChange={() =>
                handleToggleBooleanField(
                  params.row.flightInstanceId,
                  params.row.pricingType === 'LuxPlus'
                    ? luxPlusField
                    : (col.field as 'packagedAdultEconomyFree' | 'packagedChildEconomyFree'),
                  params.row.pricingType === 'LuxPlus' ? params.row[luxPlusField] : !!params.value,
                )
              }
              size="small"
            />
          ),
        };
      }
      if (col.type === 'number' && col.editable) {
        const isPercentage = PERCENTAGE_FIELDS.has(col.field as keyof CharterPricingRow);
        return {
          ...unspannedColumn,
          renderEditCell: (params) => (
            <GridEditInputCell
              {...params}
              debounceMs={0}
              onValueChange={(_event, value) => {
                const originalValue = apiRef.current.getCellValue(params.id, params.field);
                const nextValue = value === '' ? null : Number(value);
                setHasCellChanges(nextValue !== (originalValue ?? null));
              }}
            />
          ),
          valueGetter: (value: number | null | undefined) =>
            isPercentage && value != null ? percentToDisplay(value) : value,
          valueSetter: (value: number | string | null | undefined, row: CharterPricingRow) => ({
            ...row,
            [col.field]: value == null || value === '' ? null : isPercentage ? displayToPercent(Number(value)) : value,
          }),
          valueFormatter: (value: number | null | undefined) => {
            if (value == null) {
              return '';
            }
            return isPercentage ? formatPercentDisplay(value) : formatCurrency(value);
          },
        };
      }
      if (col.field === 'direction' || col.field === 'departureDate') {
        return {
          ...unspannedColumn,
          rowSpanValueGetter: (_value: unknown, row: CharterPricingRow) => row.flightInstanceId,
          renderCell: (params) =>
            col.field === 'departureDate' && !canEditCharterPricingDeparture(params.row.departureDate) ? (
              <Stack>
                <Typography variant="body2">{params.value}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Read-only
                </Typography>
              </Stack>
            ) : (
              params.value
            ),
        };
      }
      return unspannedColumn;
    });
  }, [apiRef, handleToggleBooleanField, isEditing, isSaving, isFetching]);

  const commitActiveCell = useCallback(
    (showError = true) => {
      try {
        for (const [id, fields] of Object.entries(gridEditRowsStateSelector(apiRef.current.state) ?? {})) {
          if (Object.values(fields).some((cell) => cell.error || cell.isProcessingProps)) {
            throw new Error('Correct the highlighted cell before saving or changing pages.');
          }
          for (const field of Object.keys(fields)) {
            const originalRow = apiRef.current.getRow<CharterPricingRow>(id);
            if (!originalRow) throw new Error('This departure is no longer available. Refresh and try again.');
            processRowUpdate(apiRef.current.getRowWithUpdatedValues(id, field) as CharterPricingRow, originalRow);
            if (apiRef.current.getCellMode(id, field) === GridCellModes.Edit) {
              // The page draft already holds this value; avoid a second delayed commit.
              apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
            }
          }
        }
        return true;
      } catch (error) {
        if (showError) handleProcessRowUpdateError(error instanceof Error ? error : new Error('Invalid value'));
        return false;
      }
    },
    [apiRef, processRowUpdate, handleProcessRowUpdateError],
  );

  const handleActionPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      commitActiveCell(false);
    },
    [commitActiveCell],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (!savingRef.current && commitActiveCell()) setPaginationModel(model);
    },
    [commitActiveCell, setPaginationModel],
  );

  const handleSave = useCallback(async () => {
    if (!isEditing || savingRef.current || !commitActiveCell()) return;
    const drafts = new Map(editedRowsRef.current);
    if (drafts.size === 0) {
      enqueueSnackbar('No changes to save', { variant: 'warning' });
      return;
    }

    if (Array.from(drafts.values()).some((row) => !canEditCharterPricingDeparture(row.departureDate))) {
      enqueueSnackbar('Past departures are read-only. Cancel editing and search again before saving.', {
        variant: 'error',
      });
      return;
    }
    const payload = buildUpdatePayload(drafts, luxPlusTouchedFieldsRef.current);
    savingRef.current = true;
    setIsSaving(true);

    try {
      await updatePricingMutation.mutateAsync(payload);

      // Invalidate only the pages that had edited rows
      const uniquePages = new Map<string, PageInfo>();
      for (const [flightInstanceId] of drafts) {
        const pageInfo = editedRowPagesRef.current.get(flightInstanceId);
        if (pageInfo) {
          const key = `${pageInfo.offset}|${pageInfo.limit}`;
          uniquePages.set(key, pageInfo);
        }
      }

      await Promise.all(
        Array.from(uniquePages.values()).map((pageInfo) =>
          queryClient.invalidateQueries({
            queryKey: [
              'charterFareFamilyPricing',
              DEPARTING_DEP_AIRPORT,
              DEPARTING_ARR_AIRPORT,
              RETURNING_DEP_AIRPORT,
              RETURNING_ARR_AIRPORT,
              searchFilters,
              pageInfo.offset,
              pageInfo.limit,
            ],
            refetchType: 'active',
          }),
        ),
      );

      enqueueSnackbar(`Pricing saved for ${drafts.size} ${drafts.size === 1 ? 'departure' : 'departures'}.`, {
        variant: 'success',
      });
      replaceEditedRows(new Map());
      editedRowPagesRef.current.clear();
      luxPlusTouchedFieldsRef.current.clear();
      setCellModesModel({});
      setHasCellChanges(false);
      setIsEditing(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      enqueueSnackbar(`Failed to save changes: ${errorMessage}`, { variant: 'error' });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    isEditing,
    commitActiveCell,
    enqueueSnackbar,
    queryClient,
    updatePricingMutation,
    replaceEditedRows,
    searchFilters,
  ]);

  const handleExitEditMode = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
    } else {
      setCellModesModel({});
      setIsEditing(false);
    }
  }, [hasUnsavedChanges]);

  const handleDownloadCsv = useCallback(async () => {
    if (savingRef.current || exportPricingMutation.isPending || !commitActiveCell()) return;
    try {
      const csvData = await exportPricingMutation.mutateAsync(new Map(editedRowsRef.current));
      fileDownload(Papa.unparse(csvData, { header: false }), `charter-flights-pricing-${formatDateISO()}.csv`);
      enqueueSnackbar(`CSV exported successfully with ${(csvData.length - 4) / 2} departures`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        `Failed to generate CSV: ${error instanceof Error ? error.message : 'An unexpected error occurred'}`,
        {
          variant: 'error',
        },
      );
    }
  }, [commitActiveCell, enqueueSnackbar, exportPricingMutation]);

  const hasData = !isLoading && !isError;

  useLayoutEffect(() => {
    if (!hasData) return;
    const measureTableTop = () => {
      if (tableRef.current) setTableTop(tableRef.current.getBoundingClientRect().top + window.scrollY);
    };
    measureTableTop();
    window.addEventListener('resize', measureTableTop);
    return () => window.removeEventListener('resize', measureTableTop);
  }, [hasData, filtersChanged]);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader title="Charter Flights Pricing" bottomSpacing={0} />

      <Card variant="outlined">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack
            component="form"
            spacing={2}
            onSubmit={(event) => {
              event.preventDefault();
              if (!validDates || hasUnsavedChanges || isSaving) return;
              const nextFilters: PricingSearchFilters = {
                departureDateFrom: departureDateFrom.format('YYYY-MM-DD'),
                departureDateTo: departureDateTo?.format('YYYY-MM-DD'),
                direction: direction || undefined,
              };
              setHasCellChanges(false);
              setPaginationModel({ ...paginationModel, page: 0 });
              setSearchFilters(nextFilters);
              if (!filtersChanged && paginationModel.page === 0) void refetch();
            }}
          >
            <Typography variant="subtitle1">Search departures</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DatePicker
                label="Departure date from"
                format="DD/MM/YYYY"
                value={departureDateFrom}
                onChange={setDepartureDateFrom}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    size: 'small',
                    error: !departureDateFrom?.isValid(),
                    helperText: !departureDateFrom?.isValid() ? 'Enter a valid start date.' : undefined,
                  },
                }}
              />
              <DatePicker
                label="Departure date to"
                format="DD/MM/YYYY"
                value={departureDateTo}
                onChange={setDepartureDateTo}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: Boolean(departureDateTo && !validDates),
                    helperText:
                      departureDateTo && !validDates ? 'Enter an end date on or after the start date.' : undefined,
                  },
                }}
              />
              <TextField
                select
                label="Direction"
                SelectProps={{ displayEmpty: true }}
                value={direction}
                onChange={(event) => setDirection(event.target.value as typeof direction)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Both directions</MenuItem>
                <MenuItem value="departing">{PRICING_DIRECTION_LABELS.departing}</MenuItem>
                <MenuItem value="returning">{PRICING_DIRECTION_LABELS.returning}</MenuItem>
              </TextField>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                Past departures are read-only. Today’s date uses Australia/Sydney.
                {hasUnsavedChanges && ' Save or discard edits before searching.'}
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setDepartureDateFrom(dayjs(getCharterPricingToday()));
                    setDepartureDateTo(null);
                    setDirection('');
                  }}
                >
                  Reset filters
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isFetching}
                  disabled={!validDates || hasUnsavedChanges || isSaving}
                >
                  Search
                </LoadingButton>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {filtersChanged && <Alert severity="info">Filters changed. Search to update results.</Alert>}

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading pricing data...
          </Typography>
        </Box>
      )}

      {isError && (
        <Box mt={4}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" variant="outlined" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to load pricing data</AlertTitle>
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        </Box>
      )}

      {hasData && (
        <Card variant="outlined">
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                gap={2}
              >
                <Typography variant="subtitle1">Results</Typography>
                <Stack direction="row" spacing={2}>
                  <LoadingButton
                    variant="text"
                    onPointerDown={handleActionPointerDown}
                    onClick={handleDownloadCsv}
                    loading={exportPricingMutation.isPending}
                    disabled={isSaving || isFetching}
                  >
                    Download CSV
                  </LoadingButton>
                  <Button
                    variant="contained"
                    onPointerDown={handleActionPointerDown}
                    onClick={isEditing ? handleExitEditMode : () => setIsEditing(true)}
                    disabled={isSaving || isFetching || rows.length === 0}
                  >
                    {isEditing ? 'View Mode' : 'Edit Pricing'}
                  </Button>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                CSV includes all departures and any unsaved changes, regardless of table search or filters.
              </Typography>
              <Box
                ref={tableRef}
                sx={(theme) => ({
                  width: '100%',
                  // Leave room for the card padding, page footer and a small bottom gap.
                  height: { xs: '520px', md: `max(440px, calc(100dvh - ${tableTop}px - ${theme.spacing(10)}))` },
                  display: 'flex',
                  flexDirection: 'column',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                })}
              >
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StyledDataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    localeText={{
                      noRowsLabel: 'No charter pricing found.',
                      toolbarFilters: 'Filter this page',
                      MuiTablePagination: { labelRowsPerPage: 'Departures per page:' },
                    }}
                    columnGroupingModel={charterFlightsPricingColumnGroupingModel}
                    columnHeaderHeight={COLUMN_HEADER_HEIGHT}
                    columnGroupHeaderHeight={COLUMN_GROUP_HEADER_HEIGHT}
                    rowHeight={52}
                    unstable_rowSpanning
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'departureDate', sort: 'asc' }],
                      },
                    }}
                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    rowCount={totalRowCount}
                    editMode="cell"
                    isCellEditable={(params) =>
                      isEditing && !isSaving && !isFetching && canEditCharterPricingDeparture(params.row.departureDate)
                    }
                    cellModesModel={cellModesModel}
                    onCellModesModelChange={handleCellModesModelChange}
                    onCellEditStop={(params) => {
                      const cancelled = params.reason === GridCellEditStopReasons.escapeKeyDown;
                      setIsCommittingCell(!cancelled);
                      if (cancelled) setHasCellChanges(false);
                    }}
                    onCellClick={handleCellClick}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    slots={{
                      toolbar: CharterFlightsPricingToolbar,
                    }}
                    sx={{
                      '& .MuiDataGrid-cell[data-field="separator"]': {
                        backgroundColor: 'action.hover',
                        borderLeft: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                      },
                      '& .MuiDataGrid-cell--editing': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -2,
                        boxShadow: 1,
                        backgroundColor: 'background.paper',
                      },
                    }}
                    disableRowSelectionOnClick
                  />
                </Box>
                {isEditing && rows.length > 0 && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                  >
                    <Typography variant="body2" color="text.secondary" aria-live="polite">
                      {editedRows.size
                        ? `${editedRows.size} ${editedRows.size === 1 ? 'departure' : 'departures'} with unsaved changes across all pages`
                        : hasCellChanges
                          ? 'Finish editing the cell, or save to apply it.'
                          : 'Edit pricing or sale settings in the table to make changes.'}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="text"
                        onPointerDown={handleActionPointerDown}
                        onClick={() => setIsDiscardDialogOpen(true)}
                        disabled={!hasUnsavedChanges || isSaving}
                      >
                        Discard edits
                      </Button>
                      <LoadingButton
                        variant="contained"
                        onPointerDown={handleActionPointerDown}
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={!hasUnsavedChanges || isSaving}
                      >
                        Save changes
                      </LoadingButton>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDiscardDialogOpen} onClose={handleCancelDiscard}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved pricing changes.{' '}
            {pendingNavigation
              ? 'If you leave this page, they will be discarded.'
              : 'Discard these edits and return to the saved pricing?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={handleCancelDiscard} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
